import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

// Empty string = same-origin (API proxied through CloudFront)
const API_URL = import.meta.env.VITE_API_URL ?? '';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Token storage functions
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setAccessToken = (token: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

export const setRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  // FastAPI expects arrays as repeated params: tags=a&tags=b (not tags[0]=a&tags[1]=b)
  paramsSerializer: {
    indexes: null, // Use repeated params format for arrays
  },
});

// Request interceptor - inject access token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers['X-Request-ID'] = crypto.randomUUID();
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 with shared refresh logic (refresh goes to auth service)
// Import is deferred to avoid circular dependency (authApi imports token functions from api)
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const { handleTokenRefresh } = await import('./authApi');
    return handleTokenRefresh(error, (req) => apiClient(req));
  }
);

// API helper functions for consistent usage
export async function apiGet<T>(url: string, params?: unknown, timeout?: number): Promise<T> {
  const response = await apiClient.get<T>(url, { params, timeout });
  return response.data;
}

export async function apiPost<T>(
  url: string,
  data?: unknown,
  timeout?: number,
  signal?: AbortSignal
): Promise<T> {
  const response = await apiClient.post<T>(url, data, { timeout, signal });
  return response.data;
}

export async function apiPut<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data);
  return response.data;
}

export async function apiDelete<T>(url: string): Promise<T> {
  const response = await apiClient.delete<T>(url);
  return response.data;
}

export async function apiPatch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data);
  return response.data;
}

export default apiClient;
